import { PROJECT_MEDIA_TYPES } from 'consts'
import { FIT } from 'consts/canvas'
import { ErrorKeys } from 'consts/errorKeys'
import { checkIfAIVideoIsGenerating } from 'containers/SubSidebar/AIVideoContent/utils'
import { aiVideoSelectors } from 'store/features/aiVideo/selectors'
import { aiVideoThunks } from 'store/features/aiVideo/thunks'
import {
  mapApiReferenceMediasToState,
  getAvailableDuration,
  getFirstFrameOrRefImageUrlFromApi
} from 'store/features/aiVideo/utils'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { projectDataThunks } from 'store/features/projectData/thunks'
import { TAppDispatch, TGetState } from 'store/types'
import { handleApiError } from 'store/utils'
import { generationProgressService } from 'services/generationProgress'
import {
  IAIVideoSocketMessage,
  IAIVideoProject,
  IAIVideoGenerationParams,
  AIVideoSocketMessageStatus
} from 'types/aiVideo'
import { MediaStatus } from 'types/media'
import { calculateMediaRect, resolveCompletedMediaRect } from 'utils/media'

/**
 * Extracts and updates AI video generation parameters from different socketData sources.
 * Created to avoid code duplication
 */
const updateAIVideoGenerationParams =
  (
    socketData: IAIVideoSocketMessage | IAIVideoProject,
    targetMediaItemId?: string
  ) =>
  (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const currentAspectRatio = state.aiVideo.generationParams.aspectRatio
    // First/Last-frame reference image (not multi-ref images)
    const isFramesActiveAndFilled =
      aiVideoSelectors.isFramesActiveAndFilled(state)

    const generationParams: Partial<IAIVideoGenerationParams> = {
      prompt: socketData.prompt || '',
      duration: socketData.videoDurationSeconds,
      selectedModel: socketData.videoModel || '',
      aspectRatio:
        isFramesActiveAndFilled && currentAspectRatio === null
          ? null
          : socketData.aspectRatio || '16:9',
      quality: socketData.quality,
      referenceMedias: mapApiReferenceMediasToState(
        socketData.referenceMedias,
        socketData.width !== null && socketData.height !== null
          ? { width: socketData.width, height: socketData.height }
          : undefined
      ),
      aiImageId: socketData.meta?.reference?.id || null,
      targetMediaItemId,
      isConsistencyEnabled: false
    }
    dispatch(aiVideoThunks.updateGenerationParams(generationParams))

    if (socketData.videoModel) {
      dispatch(
        aiVideoThunks.updateSelectedModel({
          modelName: socketData.videoModel,
          duration: socketData.videoDurationSeconds,
          quality: socketData.quality,
          aspectRatio: socketData.aspectRatio
        })
      )
    }
  }

const processCompletedAIVideo =
  (socketData: IAIVideoSocketMessage) =>
  async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const pendingMediaItems =
      projectDataSelectors.pendingAIVideoMediaItems(state)
    const currentMediaItems = projectDataSelectors.mediaItems(state)
    const targetMediaItemId = aiVideoSelectors.targetMediaItemId(state)

    const dataId = socketData?.id
      ? String(socketData.id)
      : socketData.gen_ai_video_hash

    const genAiVideoHash = socketData.gen_ai_video_hash

    const searchIds = [dataId, genAiVideoHash]
    if (!dataId) return

    // Prefer the present-state item so the user's in-flight resize is
    // captured; fall back to the pending lookup (covers past/future) only
    // for tracking/error handling.
    const targetItem =
      currentMediaItems.find(item => searchIds.includes(item.assetId)) ||
      pendingMediaItems.find(item => searchIds.includes(item.assetId))
    const defaultDimensions = projectDataSelectors.defaultDimensions(state)

    const { width, height } = socketData

    if (!width || !height) return

    const mediaRect = calculateMediaRect({
      mediaDimensions: { width, height },
      mode: FIT,
      canvasDimensions: defaultDimensions,
      rotation: 0
    })

    if (targetItem) {
      const duration = getAvailableDuration({
        itemStartTime: targetItem.startTime,
        newDuration: socketData.videoDurationSeconds
      })

      const resolvedRect = resolveCompletedMediaRect({
        currentWidth: targetItem.width,
        currentHeight: targetItem.height,
        currentCoordinates: targetItem.coordinates,
        newOriginal: { width, height },
        canvasDimensions: defaultDimensions,
        rotation: targetItem.rotation
      })

      dispatch(
        projectDataThunks.updateMediaAfterGeneration(
          {
            id: targetItem.id,
            updates: {
              type: PROJECT_MEDIA_TYPES.aiVideo,
              duration,
              width: resolvedRect.width,
              height: resolvedRect.height,
              assetId: String(socketData.id),
              coordinates: { x: resolvedRect.x, y: resolvedRect.y },
              meta: {
                ...targetItem.meta,
                path: socketData.videoUrl,
                previewPath:
                  socketData.previewPath ?? targetItem.meta.previewPath,
                originalHeight: height,
                originalWidth: width,
                trimStart: 0,
                originalDuration: socketData.videoDurationSeconds,
                status: MediaStatus.OK
              }
            }
          },
          {
            isUndoable: false
          }
        )
      )
      dispatch(projectDataThunks.removeFromProcessingItems(targetItem.id))
    }

    // update item data in history and progress in ls, no matter item is still in timeline or not

    generationProgressService.complete({
      mediaItemId: targetItem?.id,
      assetId: targetItem?.assetId ?? dataId
    })

    dispatch(
      aiVideoThunks.updateAiVideoHistoryItem({
        id: socketData.id,
        updates: {
          status: AIVideoSocketMessageStatus.COMPLETED,
          videoUrl: socketData.videoUrl,
          previewPath: socketData.previewPath,
          videoDurationSeconds: socketData.videoDurationSeconds,
          width: mediaRect.width,
          height: mediaRect.height
        }
      })
    )

    if (targetItem?.id === targetMediaItemId) {
      dispatch(updateAIVideoGenerationParams(socketData))
    }
  }

const updateProcessingAIVideo =
  (socketData: IAIVideoSocketMessage) =>
  async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const mediaItems = projectDataSelectors.mediaItems(state)
    const genAiVideoHash = socketData.gen_ai_video_hash
    if (!genAiVideoHash) return

    const targetItem = mediaItems.find(
      item =>
        checkIfAIVideoIsGenerating(item) && item.assetId === genAiVideoHash
    )

    const assetId = socketData?.id
    const firstFrameOrRefImageUrl = getFirstFrameOrRefImageUrlFromApi(
      socketData.referenceMedias
    )

    if (targetItem) {
      dispatch(
        projectDataThunks.updateMediaItemWithoutUndoSave({
          id: targetItem.id,
          updates: {
            type: PROJECT_MEDIA_TYPES.aiVideo,
            assetId: assetId ? String(assetId) : genAiVideoHash,
            meta: {
              ...targetItem.meta,
              path: firstFrameOrRefImageUrl,
              previewPath: undefined,
              originalHeight:
                socketData?.height ?? targetItem.meta.originalHeight,
              originalWidth: socketData?.width ?? targetItem.meta.originalWidth,
              originalDuration:
                socketData?.videoDurationSeconds ??
                targetItem.meta.originalDuration
            }
          },
          isCheckAssetId: false
        })
      )
    }

    //even if the timeline item was deleted, we need to update the progress data and history tab
    const mediaItemId = targetItem?.id || genAiVideoHash
    generationProgressService.updateAssetId(mediaItemId, assetId)
  }

const processFailedAIVideo =
  (socketData: IAIVideoSocketMessage) =>
  (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const mediaItems = projectDataSelectors.mediaItems(state)

    const dataId = socketData?.id
      ? String(socketData.id)
      : socketData?.gen_ai_video_hash

    const genAiVideoHash = socketData.gen_ai_video_hash

    const searchIds = [dataId, genAiVideoHash]
    if (!dataId) return

    dispatch(aiVideoThunks.removeAiVideoHistoryItem([dataId, genAiVideoHash]))

    const targetItem = mediaItems.find(
      item =>
        checkIfAIVideoIsGenerating(item) && searchIds.includes(item.assetId)
    )

    if (targetItem) {
      dispatch(
        projectDataThunks.handleFailedGenerationForMediaItem(targetItem.id)
      )
      dispatch(
        handleApiError(
          socketData.failureReason
            ? ErrorKeys.CUSTOM_API_ERROR
            : ErrorKeys.AI_VIDEO_GENERATION_FAILED,
          socketData.failureReason
        )
      )
    }
    // Stop generator immediately on failure (no need to show 100%)
    generationProgressService.stop({
      mediaItemId: targetItem?.id || genAiVideoHash,
      assetId: dataId,
      shouldRemoveFromStorage: true
    })
  }

export const aiVideoSocketHandlers = {
  processCompletedAIVideo,
  updateProcessingAIVideo,
  processFailedAIVideo
}
